import React from 'react';
import Paper, { PaperProps as MaterialPaperProps } from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid, { GridProps } from '@material-ui/core/Grid';
import { ModuleProps } from '../../../../types';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Organization, OrganizationType, User } from 'types/schema-generated-types';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import WithPrivilegedUser from 'components/@layout/WithPrivilegedUser';
import Config from 'config';
import OrganizationOptionsMenu from './OrganizationOptionsMenu';

export type OrganizationTypeMap =
  | OrganizationType.DISTRICT
  | OrganizationType.USER_GROUP
  | OrganizationType.SCHOOL;

type OrganizationTileProps = GridProps &
  ModuleProps & {
    title: string;
    type: OrganizationTypeMap;
    admins: Array<User>;
    PaperProps?: MaterialPaperProps;
    color?: string;
    schools?: number;
    classrooms?: number;
    organization: Organization;
    onViewReportClick?: React.MouseEventHandler<HTMLButtonElement>;
  };

const useStyles = makeStyles((theme: Theme) => {
  return {
    type: {
      color: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      padding: `0px ${theme.spacing(1)}px`
    },

    container: {
      height: '100%'
    },

    infoContainer: {
      marginTop: theme.spacing(1.5)
    },

    emailContainer: {
      margin: `${theme.spacing(0.5)}px 0px ${theme.spacing(1)}px 0px`
    },
    paper: {
      padding: theme.spacing(2),
      minWidth: '327px',
      maxWidth: '400px',
      height: 'auto',
      overflowX: 'hidden',
      overflowY: 'hidden'
    },
    username: {
      fontWeight: 500
    },
    buttonContainer: {
      display: 'flex',

      justifyContent: 'flex-end',

      width: '100%'
    },
    button: {
      float: 'right'
    },
    moreOptionsIcon: {
      color: 'black'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between'
    }
  };
});

const COLORS: Record<OrganizationTypeMap, string> = {
  [OrganizationType.DISTRICT]: '#BB6577',
  [OrganizationType.SCHOOL]: '#444345',
  [OrganizationType.USER_GROUP]: '#4DA367'
};
const OrganizationTile: React.FC<OrganizationTileProps> = ({
  onClick,
  title,
  type,
  admins,
  schools,
  onViewReportClick,
  classrooms,
  color,
  PaperProps,
  organization,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation('AdminAdminOrganizations');
  const { privilegesGroups } = Config;

  return (
    <Grid item {...rest}>
      <div className={classes.container}>
        <Paper className={classes.paper} onClick={onClick} {...PaperProps}>
          <div className={classes.header}>
            <Typography variant="h5">{title}</Typography>
            <WithPrivilegedUser privileges={privilegesGroups.mutateOrganization}>
              <OrganizationOptionsMenu organization={organization} {...rest} />
            </WithPrivilegedUser>
          </div>
          <Typography
            variant="caption"
            className={classes.type}
            style={{ backgroundColor: color || COLORS[type] }}
          >
            {t(type.replace('_', ' '))}
          </Typography>
          <div className={classes.infoContainer}>
            <div>
              <Typography variant="body1" component="span">
                {t('Administrators')}:
              </Typography>
            </div>
            <div className={classes.emailContainer}>
              {admins.map(function AdminList({
                firstName,
                lastName,
                username
              }: User): React.ReactElement {
                return (
                  <div key={`${title}-${username}`}>
                    <Typography variant="body1" component="span">
                      {t(`${firstName} ${lastName} `)}
                    </Typography>
                    <Typography variant="body1" component="span" className={classes.username}>
                      {t(`(${username})`)}
                    </Typography>
                  </div>
                );
              })}
            </div>
            {typeof classrooms === 'number' && (
              <Typography variant="body1" component="span">
                {t('No. of Classes')}:{` ${classrooms}`}
              </Typography>
            )}
            {typeof schools === 'number' && (
              <Typography variant="body1" component="span">
                {t('No. of Schools')}:{` ${schools}`}
              </Typography>
            )}
          </div>
          {onViewReportClick && (
            <div className={classes.buttonContainer}>
              <Button
                startIcon={<ArrowForwardIcon />}
                className={classes.button}
                onClick={onViewReportClick}
                size="small"
              >
                {t('View Report')}
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </Grid>
  );
};

export default OrganizationTile;
